import { useEffect, useState, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useVertical } from '../../../context/VerticalContext';
import { useOrgSettings } from '../../../hooks/useOrgSettings';
import { DEFAULT_LABOUR_DESCRIPTION } from '../Workshop/jobWorkflow';
import { reportError } from '../../../lib/errors';

export interface JobItem {
    id: string;
    type: 'part' | 'labour';
    description: string; // This might be mapped from 'item_type' or joined
    quantity: number;
    price: number; // mapped from unit_price
    costPrice?: number;
    /** Extra work the customer accepted (its request number): read-only. */
    approvedExtraNo?: number | null;
}

export interface Job {
    id: string;
    job_number?: number;
    customer_name: string;
    vehicle_vrm: string;
    vehicle_desc: string;
    status: 'pending' | 'in_progress' | 'waiting_parts' | 'ready';
    items: JobItem[];
    total: number;
    created_at: string;
    job_description?: string;
    invoice_id?: string;
    invoice_number?: string;
    invoice_status?: string;
    locked?: boolean;
    is_void?: boolean;
    vehicle_engine?: string;
    vehicle_fuel_type?: string;
    has_walkaround?: boolean;
    /** When the work was expected to be finished. Drives the overdue flag. */
    estimated_completion?: string | null;
    /** Why it's held up — free text, shown on the dashboard job list. */
    status_note?: string | null;
}

export function useWorkshop() {
    const { activeOrgId } = useVertical();
    const { settings: orgSettings } = useOrgSettings();
    const [jobs, setJobs] = useState<Job[]>([]);
    const [loading, setLoading] = useState(true);
    // Whether the last load failed. An empty list after a failed load is not
    // evidence that a job is gone (NewJobFlow's resume check relies on this).
    const [loadError, setLoadError] = useState(false);

    const fetchJobs = useCallback(async () => {
        if (!activeOrgId) return;
        setLoading(true);
        // Join with customers and vehicles
        const { data, error } = await supabase
            .from('jobs')
            .select(`
                id,
                job_number,
                status,
                created_at,
                job_description,
                estimated_completion,
                status_note,
                customers ( full_name ),
                vehicles ( vrm, make, model, engine, fuel_type ),
                job_items ( id, item_type, quantity, unit_price, cost_price, description, quote:quotes ( quote_type, sequence_no, status ) )
            `)
            .eq('org_id', activeOrgId)
            .is('deleted_at', null)
            .order('created_at', { ascending: false });

        if (error) {
            reportError(error, 'use-workshop.jobs');
            setLoadError(true);
            setLoading(false);
            return;
        }
        setLoadError(false);

        if (!data || data.length === 0) {
            setJobs([]);
            setLoading(false);
            return;
        }

        // Fetch invoices for these jobs to avoid missing foreign key relation on join
        const jobIds = data.map((j: any) => j.id);
        const { data: invoicesData, error: invoicesDataError } = await supabase
            .from('invoices')
            .select('id, job_id, invoice_number, status')
            .in('job_id', jobIds)
            .is('deleted_at', null);
        if (invoicesDataError) reportError(invoicesDataError, 'use-workshop.invoices');

        const invoiceMap = new Map<string, { id: string; number?: string; status?: string }>();
        if (invoicesData) {
            invoicesData.forEach((inv: any) => {
                invoiceMap.set(inv.job_id, { id: inv.id, number: inv.invoice_number, status: inv.status });
            });
        }

        // Map DB structure to UI Interface
        const mappedJobs: Job[] = data.map((j: any) => ({
            id: j.id,
            job_number: j.job_number,
            customer_name: j.customers ? j.customers.full_name : 'Unknown Customer',
            vehicle_vrm: j.vehicles ? j.vehicles.vrm : 'NO VRM',
            vehicle_desc: j.vehicles ? `${j.vehicles.make} ${j.vehicles.model}` : 'Unknown Vehicle',
            vehicle_engine: j.vehicles ? j.vehicles.engine : undefined,
            vehicle_fuel_type: j.vehicles ? j.vehicles.fuel_type : undefined,
            status: j.status,
            job_description: j.job_description,
            estimated_completion: j.estimated_completion,
            status_note: j.status_note,
            items: (j.job_items || []).map((item: any) => ({
                id: item.id,
                type: item.item_type,
                // Empty stays empty. This used to substitute the literal word
                // "Part", which is not a description — it landed in the editable
                // description box as a value the user had to delete before
                // typing the real name, and got saved back to the row verbatim
                // on any other edit. The box now carries a placeholder instead.
                description: item.description || '',
                quantity: item.quantity,
                price: item.unit_price,
                costPrice: item.cost_price,
                approvedExtraNo: item.quote?.quote_type === 'additional_work'
                    && (item.quote.status === 'accepted' || item.quote.status === 'converted')
                    ? item.quote.sequence_no : null,
            })),
            total: (j.job_items || []).reduce((acc: number, item: any) => acc + (item.quantity * item.unit_price), 0),
            created_at: j.created_at,
            invoice_id: invoiceMap.get(j.id)?.id,
            invoice_number: invoiceMap.get(j.id)?.number,
            invoice_status: invoiceMap.get(j.id)?.status,
            locked: invoiceMap.get(j.id)?.status === 'paid' || j.status === 'void',
            is_void: j.status === 'void',
            has_walkaround: false // Walkaround status can be fetched separately if needed
        }));

        setJobs(mappedJobs);
        setLoading(false);
    }, [activeOrgId]);

    const addJob = async (_vrm: string, links?: { customerId?: string, vehicleId?: string }) => {
        if (!activeOrgId) return;

        // INSERT JOB
        const { error } = await supabase.from('jobs').insert({
            org_id: activeOrgId,
            status: 'pending',
            // If linked, we can use the ID. If not, maybe we just store the VRM in a note/description or find the vehicle?
            // For now, let's rely on the links if provided.
            customer_id: links?.customerId,
            vehicle_id: links?.vehicleId,
            // If we don't have a vehicle id but have a VRM string, we might want to store it temporarily or look it up.
            // But for this task, we assume the 'links' are passed from the profile where we know the IDs.
        });

        if (error) {
            // Surfaces the free-tier limit and the same-organisation guard
            // (fn_guard_job_free_tier / fn_guard_same_org_refs) instead of
            // silently doing nothing.
            reportError(error, 'use-workshop.jobs-insert');
            throw error;
        }
        fetchJobs();
    };

    const addJobItem = async (jobId: string, jobCreatedAt: string, type: 'part' | 'labour', description: string = '', quantity: number = 1, price?: number, costPrice: number = 0) => {
        const itemDesc = description || (type === 'labour' ? DEFAULT_LABOUR_DESCRIPTION : '');
        const finalPrice = price !== undefined ? price : (type === 'labour' ? (orgSettings?.default_labor_rate || 0) : 0);
        
        const { error } = await supabase.from('job_items').insert({
            job_id: jobId,
            job_created_at: jobCreatedAt, // REQUIRED for Composite FK
            item_type: type,
            description: itemDesc,
            quantity,
            unit_price: finalPrice,
            cost_price: costPrice
        });
        if (error) {
            reportError(error, 'use-workshop.job-items-insert');
            throw error;
        }
        fetchJobs();
    };

    /**
     * Records why a job is running late (and, optionally, the new expected
     * date). Kept separate from updateJobStatus because a held-up job hasn't
     * changed state — it's still in progress, just slower than planned.
     */
    const updateJobProgress = async (
        jobId: string,
        jobCreatedAt: string,
        patch: { status_note?: string | null; estimated_completion?: string | null }
    ) => {
        const { error } = await supabase.from('jobs')
            .update(patch)
            .eq('id', jobId)
            .eq('created_at', jobCreatedAt);

        if (!error) fetchJobs();
        return { error };
    };

    const updateJobStatus = async (jobId: string, jobCreatedAt: string, status: string) => {
        // Use Composite Key for Partition Pruning (Fast Update)
        const { error } = await supabase.from('jobs')
            .update({ status })
            .eq('id', jobId)
            .eq('created_at', jobCreatedAt);

        if (!error) fetchJobs();
    };

    const updateJobDetails = async (jobId: string, jobCreatedAt: string, updates: { job_description?: string; current_mileage?: number; estimated_completion?: string }) => {
        // Use Composite Key for Partition Pruning
        const { error } = await supabase.from('jobs')
            .update(updates)
            .eq('id', jobId)
            .eq('created_at', jobCreatedAt);

        if (!error) fetchJobs();
    };

    const deleteJob = async (jobId: string, jobCreatedAt: string) => {
        const { error } = await supabase.from('jobs')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', jobId)
            .eq('created_at', jobCreatedAt);

        if (!error) fetchJobs();
        return { error };
    };

    const restoreJob = async (jobId: string, jobCreatedAt: string) => {
        const { error } = await supabase.from('jobs')
            .update({ deleted_at: null })
            .eq('id', jobId)
            .eq('created_at', jobCreatedAt);

        if (!error) fetchJobs();
        return { error };
    };

    useEffect(() => {
        fetchJobs();
    }, [fetchJobs]);

    return {
        jobs,
        loading,
        loadError,
        addJob,
        addJobItem,
        updateJobStatus,
        updateJobProgress,
        updateJobDetails,
        deleteJob,
        restoreJob,
        refresh: fetchJobs
    };
}
